import { computed, type Ref } from 'vue'
import { HOSTNAME_TO_PORTFOLIO, PAGE_HOSTNAME_TO_SLUG, ALL_PORTFOLIOS, type PageSlug, type PortfolioSlug } from '../components/nav/fundConfig'
import { useVisibleFunds } from './useVisibleFunds'

// Portfolio slug type + hostname map now live in the fund registry
// (nav/fundConfig.ts, GGE promotion Phase 4) — one source of truth for
// "which funds exist" instead of per-file literals.
export type Portfolio = PortfolioSlug

// Pure resolvers — exported so they can be unit-tested without touching window.
//
// Japan fund page UI Task 4 (spec 2026-09-28-japan-fund-page-ui): a hostname
// (or ?portfolio= override) that maps to a real, registered portfolio is no
// longer enough on its own — the fund must also be *visible* to the caller.
// `visible` defaults to ALL_PORTFOLIOS (the five non-gated funds) so every
// existing caller that doesn't pass it keeps behaving exactly as before.
// `allowed` is always ALL_PORTFOLIOS ∪ visible, which is the load-bearing
// bit: a non-gated fund (gge, …) must resolve correctly even when `visible`
// is empty (entitlement not loaded yet / fetch failed) — non-gated funds
// never depend on /me. A gated fund (japan) only ever appears in `allowed`
// via `visible`, so an un-entitled caller sees a known-but-hidden hostname
// fall through exactly like an unknown host.
//
// FOF (spec 2026-10-12): resolveFund returns null where it used to fall back
// to 'bga'. resolvePage (the PAGE: navigation, pill, Dashboard page choice)
// falls back to 'fof'; resolvePortfolio (the FUND context every fund panel
// reads) keeps falling back to 'bga', so no fund panel ever sees 'fof'.
export function resolveFund(hostname: string, search: string,
    visible: ReadonlyArray<PortfolioSlug> = ALL_PORTFOLIOS,
    blocked: ReadonlyArray<PortfolioSlug> = []): PortfolioSlug | null {
  // Spec 2026-09-30: a live portfolio the admin switched off (`blocked`) is removed from `allowed`,
  // so its hostname/override falls through exactly like a hidden gated fund. This replaces
  // the old "JH access not yet enabled" banner.
  const allowed = new Set<PortfolioSlug>([...ALL_PORTFOLIOS, ...visible].filter(s => !blocked.includes(s)))
  const mapped = HOSTNAME_TO_PORTFOLIO[hostname]
  if (mapped && allowed.has(mapped)) return mapped
  const override = new URLSearchParams(search).get('portfolio')
  if (override && allowed.has(override as PortfolioSlug)) return override as Portfolio
  // ?portfolio=fof is a valid PAGE override (FOF home): not a misconfig, no warning.
  if (override !== 'fof' && !Object.prototype.hasOwnProperty.call(PAGE_HOSTNAME_TO_SLUG, hostname)) {
    // Single warning so dev tools surface the misconfig but we never throw.
    // Only fires for truly unknown hosts — a known-but-hidden hostname
    // (japan for a non-entitled user) or a FOF hostname falls through
    // silently, so the console never reveals the gated fund's existence.
    console.warn(`[usePortfolio] unknown hostname "${hostname}" — defaulting to fof`)
  }
  return null
}

/** The fund context: the resolved fund, else 'bga' (also on every FOF host). */
export function resolvePortfolio(hostname: string, search: string,
    visible: ReadonlyArray<PortfolioSlug> = ALL_PORTFOLIOS,
    blocked: ReadonlyArray<PortfolioSlug> = []): Portfolio {
  return resolveFund(hostname, search, visible, blocked) ?? 'bga'
}

/** The page: FOF's hostnames and ?portfolio=fof, else the resolved fund, else 'fof' (the default landing page). */
export function resolvePage(hostname: string, search: string,
    visible: ReadonlyArray<PortfolioSlug> = ALL_PORTFOLIOS,
    blocked: ReadonlyArray<PortfolioSlug> = []): PageSlug {
  if (PAGE_HOSTNAME_TO_SLUG[hostname] === 'fof') return 'fof'
  if (!HOSTNAME_TO_PORTFOLIO[hostname] && new URLSearchParams(search).get('portfolio') === 'fof') return 'fof'
  return resolveFund(hostname, search, visible, blocked) ?? 'fof'
}

// Module-level singletons — one shared { portfolio } / { page } object per
// session so all consumers get the same reactive value. Each is a computed
// (re-resolves whenever the visible-funds list changes, e.g. once
// /api/auth/me's entitledFunds lands) rather than a plain ref set once.
let cached: { portfolio: Ref<Portfolio> } | null = null
let cachedPage: { page: Ref<PageSlug> } | null = null

export function usePortfolio(): { portfolio: Ref<Portfolio> } {
  if (!cached) {
    const vf = useVisibleFunds()
    const portfolio = computed<Portfolio>(() =>
      resolvePortfolio(window.location.hostname, window.location.search, vf.visiblePortfolios.value,
        vf.blockedPortfolios?.value ?? []))
    cached = { portfolio: portfolio as unknown as Ref<Portfolio> }
  }
  return cached
}

export function usePage(): { page: Ref<PageSlug> } {
  if (!cachedPage) {
    const vf = useVisibleFunds()
    const page = computed<PageSlug>(() =>
      resolvePage(window.location.hostname, window.location.search, vf.visiblePortfolios.value,
        vf.blockedPortfolios?.value ?? []))
    cachedPage = { page: page as unknown as Ref<PageSlug> }
  }
  return cachedPage
}

// Test-only — reset between test files that exercise window.location.
export function __resetPortfolioForTests() {
  cached = null
  cachedPage = null
}
